import { useEffect, useRef, useState } from "react";

import { ArrowLeft, KeyRound, Loader2, Mail } from "lucide-react";

import { Button } from "../components/button";
import { Card, CardContent } from "../components/card";
import { Input } from "../components/input";
import { Label } from "../components/label";
import { StatusBanner } from "../components/status-banner";

export type AuthFormViewProps = {
  mode: "signin" | "register";
  audience?: "advisor" | "client";
  email: string;
  sent: boolean;
  loading?: boolean;
  error?: string | null;
  successMsg?: string | null;
  passkeyLoading?: boolean;
  onChangeEmail: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  /** Omit to hide the "sign in with a passkey" option entirely (e.g. apps that don't support WebAuthn). */
  onPasskeySignin?: () => void;
  onSwitchMode: (next: "signin" | "register") => void;
  /** Callback for when the user clicks "Use a different email". Defaults to calling onSwitchMode(mode). */
  onCancelSent?: () => void;
  /** Override the sign-in panel headline (defaults to "Log in"). */
  signInTitle?: string;
  /** Override the sign-in panel subtext. */
  signInSubtitle?: string;
};

// Panel indices for slide direction
const PANEL = {
  signin: 0,
  register: 1,
  sent: 2,
} as const;

function AuthFormShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen">
      <div className="bg-muted flex w-full flex-col justify-center p-8 md:p-12">
        <Card className="mx-auto w-full max-w-sm overflow-hidden">
          <CardContent className="p-0">{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

function SentState({
  email,
  onResend,
  onBack,
  loading,
}: {
  email: string;
  onResend: () => void;
  onBack: () => void;
  loading?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
        <Mail className="text-primary size-8" />
      </div>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Check your inbox</h1>
        <p className="text-muted-foreground text-sm">
          We sent a sign-in link to{" "}
          <span className="text-foreground font-medium">{email}</span>. Click
          the link in the email to continue.
        </p>
      </div>
      <Button
        variant="outline"
        className="w-full"
        size="lg"
        onClick={onResend}
        disabled={loading}
      >
        {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
        Resend link
      </Button>
      <Button
        variant="ghost"
        className="text-muted-foreground h-auto p-0 text-sm"
        onClick={onBack}
        type="button"
      >
        <ArrowLeft className="mr-1 size-3" />
        Use a different email
      </Button>
    </div>
  );
}

export function AuthFormView({
  mode,
  audience = "advisor",
  email,
  sent,
  loading,
  error,
  successMsg,
  passkeyLoading,
  onChangeEmail,
  onSubmit,
  onResend,
  onPasskeySignin,
  onSwitchMode,
  onCancelSent,
  signInTitle,
  signInSubtitle,
}: AuthFormViewProps) {
  // Determine which panel index should be active
  const activePanel = sent ? PANEL.sent : PANEL[mode];

  // Track the previous panel so we only animate on real changes
  const prevPanelRef = useRef(activePanel);
  const [displayPanel, setDisplayPanel] = useState(activePanel);
  const [, setAnimating] = useState(false);

  useEffect(() => {
    if (activePanel !== prevPanelRef.current) {
      prevPanelRef.current = activePanel;
      setDisplayPanel(activePanel);
      setAnimating(true);
      const t = setTimeout(() => setAnimating(false), 300);
      return () => clearTimeout(t);
    }
  }, [activePanel]);

  // When sliding *back* from sent, we want to go to whichever mode is active
  const handleBack = () => {
    if (onCancelSent) {
      onCancelSent();
    } else {
      onSwitchMode(mode);
    }
  };

  return (
    <AuthFormShell>
      {/* Sliding track: 3 panels side-by-side */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${displayPanel * 100}%)` }}
          aria-live="polite"
        >
          {/* Panel 0 — Sign in */}
          <div
            className="w-full shrink-0 p-8"
            aria-hidden={displayPanel !== PANEL.signin}
          >
            <form
              className="flex flex-col gap-6"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              suppressHydrationWarning
              autoComplete="on"
            >
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-semibold">
                  {signInTitle ?? "Log in"}
                </h1>
                <p className="text-muted-foreground text-sm">
                  {signInSubtitle ??
                    "Enter your email and we'll send you a sign-in link."}
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  value={email}
                  onChange={(e) => onChangeEmail(e.target.value)}
                  className="h-11"
                  autoComplete="email"
                  tabIndex={displayPanel === PANEL.signin ? 0 : -1}
                />
              </div>
              <StatusBanner
                error={error}
                successMsg={sent ? null : successMsg}
              />
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
                tabIndex={displayPanel === PANEL.signin ? 0 : -1}
              >
                {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
                {loading ? "Sending..." : "Send sign-in link"}
              </Button>
              {onPasskeySignin && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card text-muted-foreground px-2">
                        Or
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={onPasskeySignin}
                    disabled={passkeyLoading}
                    tabIndex={displayPanel === PANEL.signin ? 0 : -1}
                  >
                    {passkeyLoading ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <KeyRound className="mr-2 size-4" />
                    )}
                    Sign in with a passkey
                  </Button>
                </>
              )}
              {audience === "advisor" && (
                <div className="text-center text-sm">
                  Don&apos;t have an account?{" "}
                  <Button
                    variant="link"
                    className="h-auto p-0 underline underline-offset-4"
                    onClick={() => onSwitchMode("register")}
                    type="button"
                    tabIndex={displayPanel === PANEL.signin ? 0 : -1}
                  >
                    Sign up
                  </Button>
                </div>
              )}
            </form>
          </div>

          {/* Panel 1 — Register */}
          <div
            className="w-full shrink-0 p-8"
            aria-hidden={displayPanel !== PANEL.register}
          >
            <form
              className="flex flex-col gap-6"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              suppressHydrationWarning
              autoComplete="on"
            >
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-semibold">
                  {audience === "client"
                    ? "Set up your vault"
                    : "Create your organisation account"}
                </h1>
                <p className="text-muted-foreground text-sm">
                  {audience === "client"
                    ? "Your files and trustees will be accessible after setup."
                    : "You'll manage your clients' vaults from here."}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="reg-email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="reg-email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  value={email}
                  onChange={(e) => onChangeEmail(e.target.value)}
                  className="h-11"
                  autoComplete="email"
                  tabIndex={displayPanel === PANEL.register ? 0 : -1}
                />
              </div>

              <StatusBanner
                error={error}
                successMsg={sent ? null : successMsg}
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
                tabIndex={displayPanel === PANEL.register ? 0 : -1}
              >
                {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
                {loading ? "Sending..." : "Create account"}
              </Button>

              <div className="text-center text-sm">
                Already have an account?{" "}
                <Button
                  variant="link"
                  className="h-auto p-0 underline underline-offset-4"
                  onClick={() => onSwitchMode("signin")}
                  type="button"
                  tabIndex={displayPanel === PANEL.register ? 0 : -1}
                >
                  Log in
                </Button>
              </div>
              {audience === "advisor" && (
                <p className="text-muted-foreground text-center text-xs">
                  Advisors join via an organisation invitation link.
                </p>
              )}
            </form>
          </div>

          {/* Panel 2 — Sent / magic-link dispatched */}
          <div
            className="w-full shrink-0 p-8"
            aria-hidden={displayPanel !== PANEL.sent}
          >
            <SentState
              email={email}
              onResend={onResend}
              onBack={handleBack}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </AuthFormShell>
  );
}
